import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from 'react-query'
import { getFetchConfig, QUERY_KEYS } from 'utils/api'
import { TimelineV2 as TimelineV2Route } from 'api/admin/routes'
import { useAlertContext } from 'context/AlertContext'

interface MachineInstance {
  id: string
  machineType: string
  instanceId: string
  currentState: string
  context: Record<string, unknown>
  parentMachineId?: string
  createdAt: string
  updatedAt: string
}

interface VirtualAction {
  id: string
  definition: string
  properties?: Record<string, unknown>
  status: string
  user: {
    id: string
    firstName: string
    lastName: string
  }
  provider?: {
    id: string
    name: string
  }
  created?: string
  updated?: string
}

interface TimelineV2Data {
  enabled: boolean
  message?: string
  machineInstances?: MachineInstance[]
  virtualActions?: VirtualAction[]
}

interface UseTimelineV2Interface {
  isLoading: boolean
  error: unknown
  data?: TimelineV2Data
}

const useTimelineV2 = (patientId: string): UseTimelineV2Interface => {
  const { setAlertText } = useAlertContext()
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()

  const fetchTimelineV2 = async (): Promise<TimelineV2Data | undefined> => {
    if (!isAuthenticated) {
      return undefined
    }

    const accessToken = await getAccessTokenSilently()
    return fetch(
      `${process.env.REACT_APP_SERVER_URL}${TimelineV2Route(patientId)}`,
      getFetchConfig(accessToken)
    )
      .then(async (res) => {
        const data = await res.json()

        if (res.ok) {
          return data
        } else {
          setAlertText(`Error loading timeline v2 (${res.status})`)
          return undefined
        }
      })
      .catch(() => {
        setAlertText('Error loading timeline v2')
        return undefined
      })
  }

  const { isLoading, error, data } = useQuery(
    [QUERY_KEYS.TIMELIME, 'v2', patientId],
    fetchTimelineV2
  )

  return {
    isLoading,
    error,
    data,
  }
}

export default useTimelineV2
